
import { useState } from 'react';
import { Search, Package, BarChart2, Database, Filter, Sigma, ArrowRight } from 'lucide-react';
import { cn } from '@/lib/utils';
import { Button } from '@/components/ui/button';

export type NodeCategory = 'data' | 'transform' | 'analysis' | 'visualisation';

export type NodeType = {
  id: string;
  label: string;
  category: NodeCategory;
  description: string;
  icon: React.ReactNode;
};

const nodeTypes: NodeType[] = [
  {
    id: 'database',
    label: 'Database',
    category: 'data',
    description: 'Connect to SQL, NoSQL or other data sources',
    icon: <Database className="h-4 w-4" />
  },
  {
    id: 'csv',
    label: 'CSV Import',
    category: 'data',
    description: 'Import data from CSV files',
    icon: <Database className="h-4 w-4" />
  },
  {
    id: 'filter',
    label: 'Data Filter',
    category: 'transform',
    description: 'Filter data based on conditions',
    icon: <Filter className="h-4 w-4" />
  },
  {
    id: 'transform',
    label: 'Transform',
    category: 'transform',
    description: 'Clean and transform your data',
    icon: <Package className="h-4 w-4" />
  },
  {
    id: 'abtest',
    label: 'A/B Test',
    category: 'analysis',
    description: 'Set up and run A/B test analysis',
    icon: <Sigma className="h-4 w-4" />
  },
  {
    id: 'bayesian',
    label: 'Bayesian Model',
    category: 'analysis',
    description: 'Run Bayesian statistical analysis',
    icon: <Sigma className="h-4 w-4" />
  },
  {
    id: 'chart',
    label: 'Chart',
    category: 'visualisation',
    description: 'Visualise results with charts',
    icon: <BarChart2 className="h-4 w-4" />
  }
];

type NodeExplorerProps = {
  onNodeSelect: (nodeType: NodeType) => void;
};

const NodeExplorer = ({ onNodeSelect }: NodeExplorerProps) => {
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedCategory, setSelectedCategory] = useState<NodeCategory | 'all'>('all');
  
  const categories: {id: NodeCategory | 'all', label: string}[] = [
    { id: 'all', label: 'All' },
    { id: 'data', label: 'Data Sources' },
    { id: 'transform', label: 'Transformation' },
    { id: 'analysis', label: 'Analysis' },
    { id: 'visualisation', label: 'Visualisation' }
  ];

  const filteredNodes = nodeTypes.filter(node => {
    const matchesSearch = node.label.toLowerCase().includes(searchTerm.toLowerCase()) || 
                         node.description.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesCategory = selectedCategory === 'all' || node.category === selectedCategory;
    return matchesSearch && matchesCategory;
  });

  return (
    <div className="bg-white rounded-lg border border-border shadow-sm overflow-hidden h-full flex flex-col">
      <div className="p-3 border-b border-border">
        <h3 className="font-medium text-sm mb-2">Node Explorer</h3>
        <div className="flex items-center px-2 h-8 rounded-md border border-input bg-background text-sm ring-offset-background focus-within:ring-1 focus-within:ring-ring mb-2">
          <Search className="h-4 w-4 text-muted-foreground mr-2" />
          <input
            type="text"
            placeholder="Search nodes..."
            className="flex w-full bg-transparent py-1 text-sm outline-none placeholder:text-muted-foreground"
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
          />
        </div>
        <div className="flex flex-wrap gap-1 text-xs">
          {categories.map(category => (
            <button
              key={category.id}
              onClick={() => setSelectedCategory(category.id)}
              className={cn(
                "px-2 py-1 rounded-md transition-colors",
                selectedCategory === category.id 
                  ? "bg-primary text-primary-foreground" 
                  : "bg-muted hover:bg-muted/80 text-foreground"
              )}
            >
              {category.label}
            </button>
          ))}
        </div>
      </div>
      <div className="flex-1 overflow-y-auto p-2">
        <div className="grid gap-2">
          {filteredNodes.map(node => (
            <div 
              key={node.id}
              className="p-2 rounded-md border border-border hover:border-primary/50 hover:bg-primary/5 cursor-pointer transition-colors"
              onClick={() => onNodeSelect(node)}
            >
              <div className="flex items-center gap-2">
                <div className="p-1.5 rounded-md bg-primary/10 text-primary">
                  {node.icon}
                </div>
                <div>
                  <h4 className="text-sm font-medium">{node.label}</h4>
                  <p className="text-xs text-muted-foreground">{node.description}</p>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>
      <div className="p-3 border-t border-border bg-muted/20">
        <p className="text-xs text-muted-foreground">
          Drag and drop nodes onto the canvas to build your workflow.
        </p>
      </div>
    </div>
  );
};

export default NodeExplorer;
